import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  clearMemberSessionToken,
  getMemberSessionToken,
  memberAuthConfig,
  rememberMemberSessionToken,
} from '../lib/sessionAuth';
import { canEnterAdminWorkspace } from '../lib/adminAccessRuntime';
import { clearMemberActivity } from '../lib/activityIntelligence';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Skip auth check if user just deliberately logged out
      const justLoggedOut = sessionStorage.getItem('admin_logged_out');
      if (justLoggedOut) {
        setUser(null);
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }

      // Send the short-lived member bearer token as a mobile fallback. Some
      // in-app browsers block cross-site cookies even when they were issued
      // correctly with SameSite=None.
      const response = await axios.get(`${API}/api/auth/me`, memberAuthConfig());
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      // Fallback: if admin token exists in localStorage, use Bearer auth
      const memberToken = getMemberSessionToken();
      if (memberToken && [401, 403].includes(error.response?.status)) clearMemberSessionToken();
      const adminToken = localStorage.getItem('admin_token');
      if (adminToken) {
        try {
          const res = await axios.get(`${API}/api/auth/me`, {
            headers: { Authorization: `Bearer ${adminToken}` },
          });
          setUser(res.data);
          setIsAuthenticated(true);
          return;
        } catch (bearerError) {
          // Never manufacture an administrator from an unverified browser
          // token. A rejected credential is removed; temporary network/server
          // failures keep the token for a later retry but grant no workspace.
          if ([401, 403].includes(bearerError.response?.status)) {
            localStorage.removeItem('admin_token');
            localStorage.removeItem('admin_access_snapshot');
          }
          if ([401, 403].includes(bearerError.response?.status)) clearMemberActivity();
          setUser(null);
          setIsAuthenticated(false);
          return;
        }
      }
      if ([401, 403].includes(error.response?.status)) clearMemberActivity();
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/api/auth/login`, { email, password }, {
      withCredentials: true
    });
    rememberMemberSessionToken(response.data?.session_token);
    setUser(response.data);
    setIsAuthenticated(true);
    return response.data;
  }, []);

  const register = useCallback(async (name, email, password, country = 'US', whatsappNumber = '', whatsappUpdatesOptIn = false) => {
    // Check for referral code from session storage
    let ref = null;
    try { ref = sessionStorage.getItem('rastyle_ref'); } catch {}
    const response = await axios.post(`${API}/api/auth/register`, {
      name, email, password, country, ref,
      whatsapp_number: whatsappNumber,
      whatsapp_updates_opt_in: whatsappUpdatesOptIn,
    }, {
      withCredentials: true
    });
    rememberMemberSessionToken(response.data?.session_token);
    setUser(response.data);
    setIsAuthenticated(true);
    return response.data;
  }, []);

  const loginWithGoogle = useCallback(({ destination = '/', creatorIntent = false } = {}) => {
    try {
      sessionStorage.setItem('rastyle_post_auth_redirect', destination);
      sessionStorage.setItem('rastyle_creator_intent', creatorIntent ? 'creator' : 'member');
    } catch {}
    window.location.href = `${API}/api/auth/social/google/start?destination=${encodeURIComponent(destination)}`;
  }, []);

  const processGoogleCallback = useCallback(async (sessionId) => {
    const response = await axios.post(`${API}/api/auth/session`, { session_id: sessionId }, {
      withCredentials: true
    });
    rememberMemberSessionToken(response.data?.session_token);
    setUser(response.data);
    setIsAuthenticated(true);
    return response.data;
  }, []);

  const loginWithSocial = useCallback((provider, { destination = '/', creatorIntent = false } = {}) => {
    try {
      sessionStorage.setItem('rastyle_post_auth_redirect', destination);
      sessionStorage.setItem('rastyle_creator_intent', creatorIntent ? 'creator' : 'member');
    } catch {}
    window.location.href = `${API}/api/auth/social/${encodeURIComponent(provider)}/start?destination=${encodeURIComponent(destination)}`;
  }, []);

  const processSocialHandoff = useCallback(async (handoffToken) => {
    const response = await axios.post(`${API}/api/auth/social/handoff`, {
      handoff_token: handoffToken,
    }, memberAuthConfig());
    rememberMemberSessionToken(response.data?.session_token);
    setUser(response.data);
    setIsAuthenticated(true);
    return response.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      const memberToken = getMemberSessionToken();
      const config = adminToken
        ? { headers: { Authorization: `Bearer ${adminToken}` } }
        : memberAuthConfig(memberToken ? {} : { withCredentials: true });
      await axios.post(`${API}/api/auth/logout`, {}, config);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearMemberActivity();
      setUser(null);
      setIsAuthenticated(false);
      clearMemberSessionToken();
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      sessionStorage.removeItem('rastyle_ref');
    }
  }, []);

  const setUserDirect = useCallback((userData) => {
    setUser(userData);
    setIsAuthenticated(!!userData);
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated,
    isAdmin: canEnterAdminWorkspace(user),
    adminAccess: user?.admin_access || { allowed: false, full_access: false, features: [] },
    login,
    register,
    loginWithGoogle,
    loginWithSocial,
    processGoogleCallback,
    processSocialHandoff,
    logout,
    checkAuth,
    setUserDirect
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
